import type { FormProps } from "antd"
import { Button, Form, message, Space, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { t } from "i18next"
import React, { useCallback, useEffect, useState } from "react"
import "react-quill/dist/quill.snow.css"
import { useNavigate, useParams } from "react-router"
import { Paths } from "~/constants"
import FeaturedImage from "~/modules/post/pages/FeaturedImage"
import { BaseData, FieldType, MediaData } from "~/types/types"
import baseService from "../service/base"
import BaseForm from "./BaseForm"
import Publish from "./Publish.tsx"

const EditBase = () => {
  const [quillValue, setQuillValue] = React.useState<string>("")
  const [base, setBase] = useState<BaseData | null>(null)
  const [status, setStatus] = useState("")
  const [publishDate, setPublishDate] = useState<string | null>(null)
  const [language, setLanguages] = useState("")
  const [selectedImage, setSelectedImage] = useState<MediaData | null>(null)
  const [selectedMetaImage, setSelectedMetaImage] = useState<MediaData | null>(null)
  const [form] = Form.useForm()
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const baseServices = baseService()

  const onFinish: FormProps<FieldType>["onFinish"] = async values => {
    const dataPayload = {
      name: values.name,
      slug: values.slug,
      content: values.content || "",
      publishedAt: status === "Draft" ? null : new Date().toISOString(),
      image: selectedImage,
      SEO: {
        metaTitle: values.metaTitle,
        metaDescription: values.metaDescription,
        keywords: values.keywords,
        metaRobots: values.metaRobots,
        metaViewport: values.metaViewport,
        structuredData: typeof values.structuredData === "string" ? JSON.parse(values.structuredData) : {},
      },
    }

    const isSlugChanged = values.slug !== base?.attributes?.slug
    if (isSlugChanged) {
      const isSlugUnique = async (slug: string): Promise<boolean> => {
        try {
          const response = await baseServices.checkSlug(slug)
          return response.data.length === 0
        } catch {
          return false
        }
      }

      const isUnique = await isSlugUnique(values.slug || "")

      if (!isUnique) {
        message.error(t("The path already exists. Please choose another path"))
        return
      }
    }

    try {
      await baseServices.updateBase(Number(id), { data: dataPayload })
      form.resetFields()

      navigate(Paths.BASESYSTEM.LIST.PATH)
    } catch {
      message.error(t("Unable to update base. Please try again"))
    }
  }

  const handleFetchBase = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await baseServices.fetchBaseById(id)

        setBase(response)
        if (response.attributes.publishedAt) {
          setStatus("Published")
        } else {
          setStatus("Draft")
        }

        form.setFieldsValue(response.attributes)
        form.setFieldValue("metaTitle", response.attributes.SEO?.metaTitle || "")
        form.setFieldValue("metaDescription", response.attributes.SEO?.metaDescription || "")
        form.setFieldValue("keywords", response.attributes.SEO?.keywords || "")
        form.setFieldValue("metaRobots", response.attributes.SEO?.metaRobots || "")
        form.setFieldValue("metaViewport", response.attributes.SEO?.metaViewport || "")
        form.setFieldValue("structuredData", JSON.stringify(response.attributes?.SEO?.structuredData) || "{}")

        setLanguages(response.attributes?.locale)
        setQuillValue(response.attributes?.content)
        const image = response.attributes.image?.data
        setSelectedImage(image)
        const metaImage = {
          id: response.attributes.SEO?.metaImage?.data.id ?? 0,
          ...response.attributes.SEO?.metaImage?.data.attributes,
        }
        setSelectedMetaImage(metaImage)
        setLoading(false)
      } catch {
        message.error(t("Base fetch error"))
      }
    },
    [form],
  )

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = () => {}

  const onChangeImage = (image: MediaData | null) => {
    setSelectedImage(image)
  }

  const onChangeMetaImage = (image: MediaData | null) => {
    setSelectedMetaImage(image)
  }

  useEffect(() => {
    if (id) {
      handleFetchBase(Number(id))
    }
  }, [id, handleFetchBase])

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-4">
          <Space>
            <Title level={2}>{t("Edit Base")}</Title>
            <Button
              onClick={() => navigate(Paths.BASESYSTEM.CREATE.PATH)}
              className="bg-white text-blue-700 px-3 py-1 border-[1px] border-blue-700 rounded-[3px] hover:bg-blue-800 hover:text-white duration-300"
            >
              {t("Add New Base")}
            </Button>
          </Space>
          <div className="flex justify-between">
            <BaseForm
              form={form}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              quillValue={quillValue}
              // @ts-ignore
              base={base}
              setQuillValue={setQuillValue}
              onChangeMetaImage={onChangeMetaImage}
            />
            <div className=" w-[22%] pt-2">
              <Publish
                onSubmit={form.submit}
                isCreateEdit={false}
                setStatus={setStatus}
                status={status}
                setPublishDate={setPublishDate}
                language={language}
                setLanguages={setLanguages}
              />
              <FeaturedImage selectedMedia={selectedImage} setSelectedMedia={onChangeImage} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default EditBase
